/**
 * Générateur — Il détermine, à partir de tous les modes de représentation,
 * l'image d'un nombre.
 * Attendu ID : 223 | Niveau : 3ème | Domaine : Fonctions (d13, O11)
 *
 * Source doc prof (B3S1/B3S4/B3S5). Plan auto-validé 2026-07-16 (TODO §6) :
 * « carre » (f = carré : images exactes, décimaux compris), « rationnelle »
 * (valeur interdite + images d'une fonction quotient), « tableau » (lecture
 * ligne 1/ligne 2 — la confusion testée). Lecture graphique portée par les
 * tableaux (adaptation : les courbes du doc « à caler » n'ont pas de figure).
 */

import { randomChoice, randInt, shuffle, tex, makeTexteTrou } from '../../../utils';
import { frNb, decFrag, tableFn, NOMS_FN } from './fonctions';

const ATTENDU_ID = 223;
const L_CARRE = 'Calculer des images par la fonction carré';
const L_RATIONNELLE = 'Images d\'une fonction quotient (valeur interdite)';
const L_TABLEAU = 'Lire une image dans un tableau';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « carre » ═════════

const genCarreTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const a = randInt(2, 9);
    const b = -randInt(2, 9);
    const c10 = randInt(11, 19);                           // décimal x = c10/10
    const cAff = frNb(c10 / 10);
    const cCarre = frNb((c10 * c10) / 100);
    const phrase = `$${nom}(${a}) = {{${a * a}}} \\qquad ${nom}(${b}) = {{${b * b}}}`
        + ` \\qquad ${nom}(${decFrag(cAff)}) = {{${cCarre}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>La fonction ${nom} associe à tout nombre son CARRÉ.</p>`
        // « le carré d'un négatif est positif ! » donnait la réponse de la 2e image.
        + '<p><b>Calcule les trois images.</b></p>',
        { ...opts(L_CARRE), mode: 'saisie' });
    payload._meta.params = { nom, a, b, c10 };
    return payload;
};

// ═════════ « rationnelle » — valeur interdite + images ═════════

const genRationnelleTrous = () => {
    const nom = randomChoice(NOMS_FN);
    // f(x) = (x + p)/(x − q) : interdite x = q ; images EXACTES sur x choisis.
    let p; let q; let x1; let x2; let y1; let y2;
    for (let essai = 0; essai < 80; essai++) {
        p = randInt(1, 6); q = randInt(1, 5);
        x1 = q + randomChoice([1, 2, -1, -2, 4]);          // dénominateur ±1, ±2, 4
        x2 = q + randomChoice([-4, 2, -2, 1]);
        const d1 = x1 - q; const d2 = x2 - q;
        if (x1 === x2 || d1 === 0 || d2 === 0) continue;
        const n1 = x1 + p; const n2 = x2 + p;
        if (n1 % d1 !== 0 && (n1 * 10) % d1 !== 0) continue;
        if (n2 % d2 !== 0 && (n2 * 10) % d2 !== 0) continue;
        y1 = n1 / d1; y2 = n2 / d2;
        if (Math.abs(y1) <= 30 && Math.abs(y2) <= 30 && y1 !== y2) break;
        y1 = null;
    }
    if (y1 === null || y1 === undefined) { p = 2; q = 1; x1 = 2; x2 = -1; y1 = 4; y2 = -0.5; } // repli (doc Ex.8)
    const phrase = `$\\text{Valeur interdite : } x = {{${q}}} \\qquad`
        + ` ${nom}(${decFrag(x1)}) = {{${frNb(y1)}}} \\qquad ${nom}(${decFrag(x2)}) = {{${frNb(y2)}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${nom}(x) = \\dfrac{x + ${p}}{x - ${q}}`)}.</p>`
        // « (dénominateur nul) » donnait le critère de la valeur interdite.
        + '<p><b>Pour quelle valeur la fonction n\'est-elle pas définie ? Puis calcule les '
        + 'deux images.</b> Utilise une virgule si le résultat n\'est pas entier.</p>',
        { ...opts(L_RATIONNELLE), mode: 'saisie' });
    payload._meta.params = { nom, p, q, x1, x2 };
    return payload;
};

// ═════════ « tableau » — la confusion ligne 1/ligne 2 ═════════

const genTableauTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let xs; let ys;
    for (let essai = 0; essai < 60; essai++) {
        xs = shuffle([-3, -1, 0, 2, 4, 5, 7]).slice(0, 6).sort((a, b) => a - b);
        ys = shuffle([-9, -8, -2, 3, 6, 8, 9]).slice(0, 6);
        if (!xs.some(x => ys.includes(x))) break;
    }
    const i = randInt(0, 5); let j = randInt(0, 5);
    if (j === i) j = (j + 1) % 6;
    const phrase = `$\\text{Image de } ${decFrag(xs[i])} \\text{ : } {{${frNb(ys[i])}}} \\qquad`
        + ` \\text{antécédent de } ${decFrag(ys[j])} \\text{ : } {{${frNb(xs[j])}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Voici un tableau de valeurs de la fonction ${nom} :</p>`
        + tableFn(nom, xs.map(frNb), ys.map(frNb))
        // « ne confonds pas les deux lignes ! » désamorçait le piège de la carte.
        + '<p><b>Lis l\'image et l\'antécédent demandés.</b></p>',
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { nom, xs, ys, i, j };
    return payload;
};

const GENERATORS = {
    carre_texte_trou_saisie: genCarreTrous,
    rationnelle_texte_trou_saisie: genRationnelleTrous,
    tableau_texte_trou_saisie: genTableauTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'carre', label: L_CARRE, formatsList: buildFormats('carre') },
    { id: 'rationnelle', label: L_RATIONNELLE, formatsList: buildFormats('rationnelle') },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
